import React, { useEffect, useMemo, useState } from 'react';
import { Avatar, Button, Input, Select, Spin, Switch, Tag, Upload } from 'antd';
import type { UploadProps } from 'antd';
import type { RcFile } from 'antd/es/upload';
import {
  BankOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  MailOutlined,
  PhoneOutlined,
  SaveOutlined,
  SafetyCertificateOutlined,
  UploadOutlined,
  UserOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import { UtilService } from '../../../../../core/services/util.service';
import { User } from '../model/user.model';
import { useProfile } from './ProfileContext';
import { ProfileService } from '../service/profile.service';
import {
  joinPhoneNumber,
  splitPhoneNumber,
  validateProfileDetails,
} from './profile-workspace.utils';
import './ProfileWorkspace.css';

interface CountryOption {
  value: string;
  label: React.ReactNode;
  dial_code: string;
}

const getPhotoKey = (userId: string) => `profilePhoto_${userId}`;

const storePhoto = (userId: string, dataUrl: string) => {
  try {
    localStorage.setItem(getPhotoKey(userId), dataUrl);
  } catch (error) {
    console.warn('Profile photo could not be cached:', error);
  }
};

const roleLabel = (user: User) =>
  user.admin ? 'Company administrator' : 'Recruiter';

const MyProfile: React.FC = () => {
  const { profile, setProfile } = useProfile();
  const [user, setUser] = useState<User>(new User());
  const [initialUser, setInitialUser] = useState<User>(new User());
  const [photo, setPhoto] = useState('');
  const [selectedCountryCode, setSelectedCountryCode] = useState('CR');
  const [initialCountryCode, setInitialCountryCode] = useState('CR');
  const [saving, setSaving] = useState(false);
  const [photoMutation, setPhotoMutation] = useState(false);

  const countryOptions: CountryOption[] = useMemo(
    () =>
      require('../../../../../assets/countries.json').map((country: any) => ({
        value: country.code,
        dial_code: country.dial_code,
        label: (
          <span className="profile-country-option">
            <img src={country.flag} alt="" aria-hidden />
            <span>{country.dial_code}</span>
          </span>
        ),
      })),
    []
  );

  useEffect(() => {
    if (!profile) return;

    const phone = splitPhoneNumber(profile.phoneNumber || '', countryOptions);
    const editableUser = { ...profile, phoneNumber: phone.localNumber };

    const countryCode = phone.countryCode;
    setSelectedCountryCode(countryCode);
    setInitialCountryCode(countryCode);
    setUser(editableUser);
    setInitialUser(editableUser);

    const cachedPhoto = localStorage.getItem(getPhotoKey(profile.id));
    if (cachedPhoto) {
      setPhoto(cachedPhoto);
    } else if (profile.userFile?.file?.id) {
      void loadPhoto(profile.id, profile.userFile.file.id);
    }
  }, [countryOptions, profile]);

  const loadPhoto = async (userId: string, fileId: string) => {
    try {
      const blob = await ProfileService.GetFile(userId, fileId);
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result as string;
        setPhoto(dataUrl);
        storePhoto(userId, dataUrl);
      };
      reader.readAsDataURL(blob);
    } catch (error: any) {
      if (error?.message !== 'NOT FOUND') {
        console.error('Failed to load profile photo:', error);
      }
    }
  };

  const fullPhone = (value: User, countryCode = selectedCountryCode) => {
    return joinPhoneNumber(
      value.phoneNumber || '',
      countryCode,
      countryOptions
    );
  };

  const isDirty =
    user.firstname.trim() !== initialUser.firstname.trim() ||
    user.lastname.trim() !== initialUser.lastname.trim() ||
    user.email.trim() !== initialUser.email.trim() ||
    fullPhone(user) !== fullPhone(initialUser, initialCountryCode) ||
    user.whatsappNotifiable !== initialUser.whatsappNotifiable;

  const updateField = (field: keyof User, value: string | boolean) => {
    setUser((current) => ({ ...current, [field]: value }));
  };

  const saveProfile = async () => {
    const errors = validateProfileDetails(user);
    if (errors.length) {
      UtilService.Alert(
        'Review profile details',
        'info',
        errors.join(' </br> ')
      );
      return;
    }

    const updatedUser = {
      ...user,
      firstname: user.firstname.trim(),
      lastname: user.lastname.trim(),
      email: user.email.trim(),
      phoneNumber: fullPhone(user),
      emailNotifiable: true,
    };

    setSaving(true);
    try {
      await ProfileService.Patch(updatedUser);
      setInitialUser({ ...user, emailNotifiable: true });
      setInitialCountryCode(selectedCountryCode);
      setProfile(updatedUser);
      UtilService.Alert(
        'Profile updated',
        'success',
        'Your account details are up to date.'
      );
    } catch (error) {
      console.error('Failed to update profile:', error);
      UtilService.Alert('Profile not updated', 'error', 'Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const validatePhoto = (candidate: RcFile) => {
    const validType = ['image/jpeg', 'image/png'].includes(candidate.type);
    const validSize = candidate.size / 1024 / 1024 < 2;
    if (!validType) {
      UtilService.Alert('Unsupported image', 'error', 'Use a JPG or PNG file.');
    } else if (!validSize) {
      UtilService.Alert(
        'Image too large',
        'error',
        'Use an image smaller than 2 MB.'
      );
    }
    return validType && validSize;
  };

  const uploadPhoto: UploadProps['onChange'] = async ({ file: uploadFile }) => {
    if (uploadFile.status !== 'done' || !uploadFile.originFileObj || !user.id)
      return;
    setPhotoMutation(true);
    try {
      const response = await ProfileService.PatchFile(
        user.id,
        uploadFile.originFileObj
      );
      const nextUser = { ...user, userFile: response };
      setUser(nextUser);
      setInitialUser((current) => ({ ...current, userFile: response }));
      setProfile({ ...(profile || nextUser), userFile: response });

      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result as string;
        setPhoto(dataUrl);
        storePhoto(user.id, dataUrl);
      };
      reader.readAsDataURL(uploadFile.originFileObj);
      UtilService.Alert(
        'Photo updated',
        'success',
        'Your new profile photo is ready.'
      );
    } catch (error) {
      console.error('Failed to upload profile photo:', error);
      UtilService.Alert(
        'Photo not updated',
        'error',
        'Please try another image.'
      );
    } finally {
      setPhotoMutation(false);
    }
  };

  const removePhoto = async () => {
    const fileId = user.userFile?.file?.id;
    if (!user.id || !fileId) return;
    setPhotoMutation(true);
    try {
      await ProfileService.DeleteFile(user.id, fileId);
      const nextUser = { ...user, userFile: undefined };
      setUser(nextUser);
      setInitialUser((current) => ({ ...current, userFile: undefined }));
      if (profile) setProfile({ ...profile, userFile: undefined });
      setPhoto('');
      localStorage.removeItem(getPhotoKey(user.id));
      UtilService.Alert(
        'Photo removed',
        'success',
        'Your initials will be shown instead.'
      );
    } catch (error) {
      console.error('Failed to remove profile photo:', error);
      UtilService.Alert('Photo not removed', 'error', 'Please try again.');
    } finally {
      setPhotoMutation(false);
    }
  };

  if (!profile) {
    return (
      <div
        className="profile-workspace__loading"
        role="status"
        aria-label="Loading profile"
      >
        <Spin size="large" />
        <span>Loading your account…</span>
      </div>
    );
  }

  const displayName =
    `${user.firstname} ${user.lastname}`.trim() || user.username;
  const initials =
    `${user.firstname?.[0] || ''}${user.lastname?.[0] || ''}` || 'U';

  return (
    <section className="my-profile" aria-labelledby="my-profile-title">
      <div className="my-profile__hero">
        <div className="my-profile__identity">
          <Avatar
            size={88}
            src={photo || undefined}
            icon={!initials ? <UserOutlined /> : undefined}
          >
            {photo ? null : initials}
          </Avatar>
          <div>
            <span>Personal account</span>
            <h2 id="my-profile-title">{displayName}</h2>
            <p>{user.email}</p>
            <div className="my-profile__tags">
              <Tag
                icon={<CheckCircleOutlined />}
                className="profile-status-tag"
              >
                Active
              </Tag>
              <Tag icon={<SafetyCertificateOutlined />}>{roleLabel(user)}</Tag>
            </div>
          </div>
        </div>
        <div className="my-profile__photo-actions">
          <Upload
            accept="image/png,image/jpeg"
            showUploadList={false}
            beforeUpload={validatePhoto}
            onChange={uploadPhoto}
            customRequest={({ onSuccess }) => onSuccess?.({})}
          >
            <Button icon={<UploadOutlined />} loading={photoMutation}>
              {photo ? 'Replace photo' : 'Upload photo'}
            </Button>
          </Upload>
          {photo && user.userFile?.file?.id ? (
            <Button
              danger
              type="text"
              icon={<DeleteOutlined />}
              disabled={photoMutation}
              onClick={() => void removePhoto()}
            >
              Remove
            </Button>
          ) : null}
          <small>JPG or PNG · maximum 2 MB</small>
        </div>
      </div>

      <div className="my-profile__grid">
        <section
          className="profile-card"
          aria-labelledby="personal-details-title"
        >
          <header className="profile-card__header">
            <span className="profile-card__icon">
              <UserOutlined />
            </span>
            <div>
              <h3 id="personal-details-title">Personal details</h3>
              <p>
                Keep your recruiter identity and contact information current.
              </p>
            </div>
          </header>
          <div className="profile-form-grid">
            <label>
              <span>First name</span>
              <Input
                value={user.firstname}
                onChange={(event) =>
                  updateField('firstname', event.target.value)
                }
              />
            </label>
            <label>
              <span>Last name</span>
              <Input
                value={user.lastname}
                onChange={(event) =>
                  updateField('lastname', event.target.value)
                }
              />
            </label>
            <label className="profile-form-grid__wide">
              <span>Email address</span>
              <Input
                prefix={<MailOutlined />}
                type="email"
                value={user.email}
                onChange={(event) => updateField('email', event.target.value)}
              />
            </label>
            <label className="profile-form-grid__wide">
              <span>Phone number</span>
              <div className="profile-phone-field">
                <Select
                  aria-label="Country calling code"
                  value={selectedCountryCode}
                  options={countryOptions}
                  onChange={setSelectedCountryCode}
                  showSearch
                  optionFilterProp="value"
                />
                <Input
                  prefix={<PhoneOutlined />}
                  value={user.phoneNumber}
                  onChange={(event) =>
                    updateField(
                      'phoneNumber',
                      event.target.value.replace(/[^0-9\s-]/g, '')
                    )
                  }
                />
              </div>
            </label>
          </div>
        </section>

        <aside
          className="profile-card profile-card--preferences"
          aria-labelledby="preferences-title"
        >
          <header className="profile-card__header">
            <span className="profile-card__icon">
              <BankOutlined />
            </span>
            <div>
              <h3 id="preferences-title">Account context</h3>
              <p>Your current workspace and communication preferences.</p>
            </div>
          </header>
          <dl className="profile-account-meta">
            <div>
              <dt>Company</dt>
              <dd>{user.companyName || 'Not assigned'}</dd>
            </div>
            <div>
              <dt>Role</dt>
              <dd>{roleLabel(user)}</dd>
            </div>
            <div>
              <dt>Username</dt>
              <dd>{user.username || 'Not available'}</dd>
            </div>
          </dl>
          <div className="profile-preferences">
            <h4>Notifications</h4>
            <div className="profile-preference-row">
              <span className="profile-preference-row__icon">
                <MailOutlined />
              </span>
              <div>
                <strong>Email</strong>
                <small>Required account and hiring updates</small>
              </div>
              <Tag className="profile-status-tag">Always on</Tag>
            </div>
            <div className="profile-preference-row">
              <span className="profile-preference-row__icon">
                <WhatsAppOutlined />
              </span>
              <div>
                <strong>WhatsApp</strong>
                <small>Receive operational notifications</small>
              </div>
              <Switch
                checked={user.whatsappNotifiable}
                onChange={(checked) =>
                  updateField('whatsappNotifiable', checked)
                }
                aria-label="WhatsApp notifications"
              />
            </div>
          </div>
        </aside>
      </div>

      <footer
        className={`my-profile__save-bar ${isDirty ? 'is-visible' : ''}`}
        aria-live="polite"
      >
        <div>
          <strong>
            {isDirty ? 'You have unsaved changes' : 'Profile is up to date'}
          </strong>
          <span>
            {isDirty
              ? 'Save to publish your account updates.'
              : 'Changes will appear across the ATS.'}
          </span>
        </div>
        <Button
          type="primary"
          icon={<SaveOutlined />}
          loading={saving}
          disabled={!isDirty}
          onClick={() => void saveProfile()}
        >
          Save changes
        </Button>
      </footer>
    </section>
  );
};

export default MyProfile;
